export const META_PIXEL_ID = "1581871499744555";

import { gaPrivateEventsView, gaShowView } from "./ga4";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export function trackPageView() {
  window.fbq?.("track", "PageView");
}

export function trackLead() {
  window.fbq?.("track", "Lead");
}

const viewContentTrackedKeys = new Set<string>();

export function trackViewContent(eventName: string, dedupeKey?: string) {
  const key = dedupeKey ?? eventName;
  if (viewContentTrackedKeys.has(key)) return;
  viewContentTrackedKeys.add(key);

  window.fbq?.("track", "ViewContent", {
    content_name: eventName,
    content_type: "event",
  });
  gaShowView(eventName);
}

export function trackPrivateEventsView() {
  window.fbq?.("trackCustom", "PrivateEventsView");
  gaPrivateEventsView();
}

export type PurchaseContentItem = {
  id: string;
  quantity: number;
  item_price?: number;
};

export type PurchaseParams = {
  value: number;
  currency?: string;
  num_items?: number;
  content_ids?: string[];
  content_name?: string;
  content_type?: string;
  contents?: PurchaseContentItem[];
};

export type OrderPurchaseInput = {
  id: string;
  amount: number;
  quantity: number;
  items?: Array<{
    title?: string;
    show_title?: string;
    variant_name?: string | null;
    quantity: number;
    amount?: number;
    show_date?: string;
    location_name?: string | null;
  }>;
};

function showTitleFromItem(
  item: NonNullable<OrderPurchaseInput["items"]>[number],
): string {
  return item.title?.trim() || item.show_title?.trim() || "Comedy Show";
}

/** Build Meta Purchase params from verified Stripe order data. */
export function buildPurchaseParamsFromOrder(
  order: OrderPurchaseInput,
  sessionId?: string | null,
): PurchaseParams {
  const items = order.items ?? [];
  const contents: PurchaseContentItem[] = items.map((item) => {
    const id = showTitleFromItem(item);
    const quantity = item.quantity || 1;
    const lineTotal = item.amount ?? 0;
    const item_price = quantity > 0 && lineTotal > 0 ? lineTotal / quantity : undefined;
    return {
      id,
      quantity,
      ...(item_price != null ? { item_price } : {}),
    };
  });

  const titles = items.map(showTitleFromItem);
  const content_name =
    titles.length === 0
      ? undefined
      : titles.length === 1
        ? titles[0]
        : titles.join(", ");

  return {
    value: order.amount,
    currency: "USD",
    num_items: order.quantity,
    content_ids: titles.length > 0 ? titles : sessionId ? [sessionId] : [order.id],
    content_name,
    content_type: "event",
    ...(contents.length > 0 ? { contents } : {}),
  };
}

export const PURCHASE_FALLBACK_PARAMS: PurchaseParams = {
  value: 1,
  currency: "USD",
};

export function trackPurchase(params: PurchaseParams) {
  window.fbq?.("track", "Purchase", {
    currency: "USD",
    ...params,
  });
}

/** Fire route-specific Meta events (including on first load). PageView is separate. */
export function trackRouteMetaEvents(pathname: string) {
  if (pathname === "/private-events" || pathname === "/contact") {
    trackPrivateEventsView();
  }
}
